/**
 * D14 — Distribution Completed / Proof.
 *
 * The photo grid appears only when the server sends photos, and the server
 * sends none unless the donor consented on this donation. When they are
 * withheld the screen says so plainly instead of silently showing nothing —
 * a donor who opted out should recognise their own choice, not think the
 * charity failed to photograph anything.
 */

import { Image, ScrollView, StyleSheet, View } from 'react-native';

import { formatDate, formatPeople } from '@mfa/domain';
import { mealImageSource } from '../../assets';
import { colors, radius, space } from '../../theme';
import { Button } from '../../components/common/Button';
import { Card, CardDivider } from '../../components/common/Card';
import { CheckIcon, Glyph } from '../../components/common/Icon';
import { EmptyState, ErrorState, LoadingState } from '../../components/feedback/States';
import { ScreenShell } from '../../components/layout/ScreenShell';
import { Text } from '../../components/common/Text';
import { useProof } from '../../core/api/hooks';
import type { DonorScreenProps } from '../../app/navigation/types';
import { useT } from '../../core/i18n/LanguageProvider';

export function DistributionProofScreen({
  navigation,
  route,
}: DonorScreenProps<'DistributionProof'>) {
  const t = useT();
  const { donationId } = route.params;
  const query = useProof(donationId);

  if (query.isLoading) {
    return (
      <ScreenShell leading="back" onBackPress={navigation.goBack} testID="screen-proof">
        <LoadingState label={t('Loading distribution details')} />
      </ScreenShell>
    );
  }

  if (query.isError) {
    const notReady = (query.error as { code?: string })?.code === 'proof.not_available';
    return (
      <ScreenShell leading="back" onBackPress={navigation.goBack} testID="screen-proof">
        {notReady ? (
          <EmptyState
            title={t('Not distributed yet')}
            message={t("We'll share the details as soon as your meals have been distributed.")}
            actionLabel={t('Back to Tracking')}
            onAction={navigation.goBack}
          />
        ) : (
          <ErrorState onRetry={() => void query.refetch()} />
        )}
      </ScreenShell>
    );
  }

  const proof = query.data!;

  return (
    <ScreenShell
      leading="back"
      onBackPress={navigation.goBack}
      testID="screen-proof"
      title={t('Donation Completed!')}
      pinnedFooter={
        proof.certificateUrl ? (
          <Button
            label={t('Download Certificate')}
            leadingIcon={<Glyph name="download" size={18} />}
            onPress={() => {
              /* Handed to the platform's download handler at integration. */
            }}
            testID="cta-certificate"
          />
        ) : undefined
      }
    >
      <View style={styles.hero}>
        <CheckIcon size={96} />
        <Text variant="body" align="center">
          {formatPeople(proof.peopleServed)} have been served.
        </Text>
      </View>

      <Card style={styles.card}>
        <DetailRow icon="calendar" label={t('Distribution Date')} value={formatDate(proof.distributionDate)} />
        {proof.location ? (
          <>
            <CardDivider />
            <DetailRow icon="location" label={t('Distribution Location')} value={proof.location} />
          </>
        ) : null}
        {proof.partnerOrganization ? (
          <>
            <CardDivider />
            <DetailRow icon="distribute" label={t('Partner')} value={proof.partnerOrganization} />
          </>
        ) : null}
        <CardDivider />
        <DetailRow icon="people" label={t('People Served')} value={String(proof.peopleServed)} />
        {proof.verifiedAt ? (
          <>
            <CardDivider />
            <DetailRow icon="hygienic" label={t('Verified')} value={formatDate(proof.verifiedAt)} />
          </>
        ) : null}
      </Card>

      {proof.note ? (
        <Card title={t('A note from the team')} style={styles.card}>
          <Text variant="body" tone="secondary">
            {proof.note}
          </Text>
        </Card>
      ) : null}

      <View style={styles.photosSection}>
        <Text variant="sectionTitle">{t('Distribution Photos')}</Text>

        {proof.photos.length > 0 ? (
          <>
            <Text variant="caption" tone="secondary" style={styles.photosCaption}>{t('These photos were shared with your permission.')}</Text>
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={styles.photoStrip}
              testID="proof-photos"
            >
              {proof.photos.map((photo, index) => (
                <Image
                  key={photo.id}
                  source={mealImageSource(photo.url) ?? { uri: photo.url }}
                  style={styles.photo}
                  accessibilityIgnoresInvertColors
                  accessible
                  accessibilityLabel={
                    photo.caption ?? `Distribution photograph ${index + 1} of ${proof.photos.length}`
                  }
                />
              ))}
            </ScrollView>
          </>
        ) : proof.photosWithheldReason === 'NO_CONSENT' ? (
          <View style={styles.withheld} testID="photos-withheld">
            <Glyph name="privacy" size={20} />
            <Text variant="caption" tone="secondary" style={styles.withheldText}>
              You chose not to receive distribution photos for this donation. You can change
              this for future donations in Settings.
            </Text>
          </View>
        ) : (
          <View style={styles.withheld} testID="photos-pending">
            <Glyph name="info" size={20} />
            <Text variant="caption" tone="secondary" style={styles.withheldText}>{t('Photos from this distribution are not available yet.')}</Text>
          </View>
        )}
      </View>

      <View style={styles.thanks}>
        <Glyph name="care" size={24} />
        <Text variant="body" tone="brand" align="center">{t('Thank you for making a difference!')}</Text>
      </View>
    </ScreenShell>
  );
}

function DetailRow({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <View style={styles.detailRow}>
      <Glyph name={icon} size={18} />
      <View style={styles.detailText}>
        <Text variant="caption" tone="tertiary">
          {label}
        </Text>
        <Text variant="bodyStrong">{value}</Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  hero: {
    alignItems: 'center',
    gap: space.sm,
    paddingVertical: space.xl,
  },
  title: {
    marginTop: space.md,
  },
  card: {
    marginTop: space.lg,
  },
  detailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.md,
    paddingVertical: space.md,
  },
  detailText: {
    flex: 1,
    gap: space.xxs,
  },
  photosSection: {
    marginTop: space.xl,
  },
  photosCaption: {
    marginTop: space.xs,
  },
  photoStrip: {
    gap: space.sm,
    paddingVertical: space.md,
  },
  photo: {
    width: 140,
    height: 110,
    borderRadius: radius.md,
    resizeMode: 'cover',
  },
  withheld: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.sm,
    marginTop: space.md,
    padding: space.md,
    borderRadius: radius.md,
    backgroundColor: colors.bg.surfaceSunken,
  },
  withheldText: {
    flex: 1,
  },
  thanks: {
    alignItems: 'center',
    gap: space.sm,
    marginTop: space['2xl'],
  },
});
